import { ArrowUpRight, BookOpen, Check } from "lucide-react";
import { SITE } from "@/lib/site";

export default function BookGuide() {
  const b = SITE.book;
  return (
    <section
      id="guide"
      data-testid="book-guide-section"
      className="relative py-24 md:py-32 bg-ink text-white overflow-hidden"
    >
      {/* Subtle texture & accent shapes */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-brass/[0.06] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
        {/* Faux book cover */}
        <div className="md:col-span-5 flex justify-center md:justify-start">
          <a
            href={b.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="book-cover-link"
            className="group block perspective-1000"
          >
            <div className="relative w-[280px] sm:w-[320px] aspect-[3/4.4] transition-transform duration-700 group-hover:-rotate-y-3 group-hover:scale-[1.02]">
              {/* Spine shadow */}
              <div className="absolute inset-y-2 -left-2 w-3 bg-gradient-to-r from-black/60 to-transparent rounded-l-sm" />
              {/* Bright outer ring to lift the cover off the dark section background */}
              <div className="absolute -inset-px rounded-sm bg-gradient-to-b from-brass/50 via-white/10 to-brass/30 pointer-events-none" />
              {/* Cover */}
              <div className="relative h-full rounded-r-sm rounded-l-sm border border-white/15 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
                {/* Background photo — aerial view of Destin Harbor & Crab Island */}
                <img
                  src="https://commons.wikimedia.org/wiki/Special:FilePath/Destinflorida.jpg?width=1200"
                  alt="Aerial view of Destin Harbor and Crab Island"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Color grade — emerald/teal duotone over the photo */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f2e]/30 via-[#0a1f2e]/55 to-[#040a14]/90" />
                <div className="absolute inset-0 bg-gradient-to-tr from-ink/40 via-transparent to-brass/10" />
                <div className="absolute inset-0 grain opacity-40" />
                {/* Top brass band */}
                <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-brass via-brass/60 to-brass" />

                <div className="relative h-full flex flex-col p-7">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-brass drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                    {b.edition} · Private Briefing
                  </div>

                  <div className="mt-auto">
                    <BookOpen className="w-6 h-6 text-brass/90 mb-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" />
                    <h3 className="font-display text-white text-3xl sm:text-4xl leading-[1.05] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]">
                      {b.title}
                    </h3>
                    <div className="mt-3 h-px w-12 bg-brass" />
                    <p className="mt-3 text-white/85 text-xs tracking-widest uppercase drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                      {b.subtitle}
                    </p>
                  </div>

                  <div className="mt-8 pt-5 border-t border-white/20 flex items-center justify-between">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-white/70 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                      By J.J. Botelho
                    </span>
                    <span className="font-display italic text-brass text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                      Vol. I
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>

        {/* Copy + CTA */}
        <div className="md:col-span-7">
          <p className="overline text-brass" data-testid="book-overline">
            Free Relocation Guide
          </p>
          <h2
            data-testid="book-heading"
            className="mt-5 font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Thinking about
            <br />
            <span className="italic text-brass">moving to Florida?</span>
          </h2>
          <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">
            {b.description}
          </p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl">
            {b.bullets.map((line) => (
              <li
                key={line}
                className="flex items-start gap-3 text-white/85 text-sm leading-relaxed"
              >
                <Check className="w-4 h-4 text-brass flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <a
              href={b.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-cta-btn"
              className="group inline-flex items-center gap-2 bg-brass text-ink px-7 py-4 rounded-full text-sm font-semibold tracking-wide hover:bg-white transition-colors duration-300"
            >
              {b.cta}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <span className="text-white/50 text-xs tracking-wide">
              Instant access · Confidential · No spam
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
